import { useEffect, useState } from 'react';
import { fetchAPIPromise, postAPI, deleteAPI, putAPI } from '../../utils/utils';

function IpWatchlist({ userInfo, onWatchlistChange, refreshTrigger }) {
    const [watchlist, setWatchlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newIp, setNewIp] = useState('');
    const [newLabel, setNewLabel] = useState('');
    const [filter, setFilter] = useState('');
    const [editingIp, setEditingIp] = useState(null);
    const [editingLabel, setEditingLabel] = useState('');

    useEffect(() => {
        loadWatchlist();
    }, []);

    // Watch for refreshTrigger changes to reload the watchlist, bypassing cache so
    // we immediately see the latest changes.
    useEffect(() => {
        if (refreshTrigger) {
            loadWatchlist(false);
        }
    }, [refreshTrigger]);

    // Helper to (re)load the watchlist. Being a function declaration means it is
    // hoisted, so the effects above can reference it without TDZ issues.
    async function loadWatchlist(useCache = true) {
        setLoading(true);
        try {
            const response = await fetchAPIPromise('/ip-watchlist', useCache);
            setWatchlist(response.ips || []);
        } catch (error) {
            console.error('Error loading watchlist:', error);
            setWatchlist([]);
        }
        setLoading(false);
    }

    const addToWatchlist = async () => {
        if (!newIp.trim()) return;
        
        try {
            postAPI('/ip-watchlist', {
                ip: newIp.trim(), 
                label: newLabel.trim() 
            }, (result) => {
                // Update local watchlist from response
                if (result && result.watchlist) {
                    setWatchlist(result.watchlist);
                }
                setNewIp('');
                setNewLabel('');
                // Trigger a fresh reload to update the cache as well
                loadWatchlist(false);
                // Notify parent component with the updated data
                if (onWatchlistChange) {
                    onWatchlistChange(result);
                }
            });
        } catch (error) {
            console.error('Error adding to watchlist:', error);
            alert('❌ Error afegint IP a la watchlist');
        }
    };

    const removeFromWatchlist = async (ip) => {
        if (!window.confirm(`Segur que vols eliminar ${ip} de la watchlist?`)) return;
        
        try {
            deleteAPI(`/ip-watchlist/${encodeURIComponent(ip)}`, (result) => {
                // Update local watchlist from response
                if (result && result.watchlist) {
                    setWatchlist(result.watchlist);
                }
                // Trigger a fresh reload to update the cache as well
                loadWatchlist(false);
                // Notify parent component with the updated data
                if (onWatchlistChange) {
                    onWatchlistChange(result);
                }
            });
        } catch (error) {
            console.error('Error removing from watchlist:', error);
            alert('❌ Error eliminant IP de la watchlist');
        }
    };

    const updateLabel = async (ip, newLabelValue) => {
        try {
            putAPI(`/ip-watchlist/${encodeURIComponent(ip)}`, {
                label: newLabelValue
            }, (result) => {
                // Update local watchlist from response
                if (result && result.watchlist) {
                    setWatchlist(result.watchlist);
                }
                setEditingIp(null);
                setEditingLabel('');
                // Trigger a fresh reload to update the cache as well
                loadWatchlist(false);
                // Notify parent component with the updated data
                if (onWatchlistChange) {
                    onWatchlistChange(result);
                }
            });
        } catch (error) {
            console.error('Error updating label:', error);
            alert('❌ Error actualitzant label');
        }
    };

    const startEditing = (ip, currentLabel) => {
        setEditingIp(ip);
        setEditingLabel(currentLabel || '');
    };

    const cancelEditing = () => {
        setEditingIp(null);
        setEditingLabel('');
    };

    const filteredWatchlist = watchlist.filter(item => 
        item.ip.toLowerCase().includes(filter.toLowerCase()) ||
        (item.label && item.label.toLowerCase().includes(filter.toLowerCase()))
    );

    return (
        <div className="ip-watchlist-container">
            <div className="watchlist-header">
                <h2>👁️ IP Watchlist</h2>
                <div className="watchlist-stats">
                    <span className="stat-badge">
                        {watchlist.length} IPs vigilades
                    </span>
                </div>
            </div>

            {/* Add IP Section */}
            <div className="add-ip-section">
                <h3>➕ Afegir IP a vigilar</h3>
                <div className="add-ip-form">
                    <input
                        type="text"
                        placeholder="IP (ex: 192.168.1.1)"
                        value={newIp}
                        onChange={(e) => setNewIp(e.target.value)}
                        className="ip-input"
                    />
                    <input
                        type="text"
                        placeholder="Label (ex: Wifi de la colla rival)"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        className="label-input"
                    />
                    <button 
                        onClick={addToWatchlist}
                        className="add-button"
                        disabled={!newIp.trim()}
                    >
                        ➕ Afegir
                    </button>
                </div>
            </div>

            {/* Filter Section */}
            <div className="filter-section">
                <input
                    type="text"
                    placeholder="🔍 Filtrar per IP o label..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="filter-search"
                />
                <div className="filter-stats">
                    Mostrant {filteredWatchlist.length} de {watchlist.length} IPs
                </div>
            </div>

            {/* Watchlist Table */}
            <div className="watchlist-table-container">
                {loading ? (
                    <div className="loading">Carregant watchlist...</div>
                ) : filteredWatchlist.length === 0 ? (
                    <div className="empty-state">
                        {filter ? 
                            `No s'han trobat IPs que coincideixin amb "${filter}"` : 
                            'No hi ha IPs a la watchlist'
                        }
                    </div>
                ) : (
                    <table className="watchlist-table">
                        <thead>
                            <tr>
                                <th>IP</th>
                                <th>Label</th>
                                <th>Afegida</th>
                                <th>Accions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredWatchlist.map((item, index) => (
                                <tr key={index} className="watchlist-row">
                                    <td className="ip-cell">
                                        <span className="ip-address">{item.ip}</span>
                                    </td>
                                    <td className="label-cell">
                                        {editingIp === item.ip ? (
                                            <div className="edit-label">
                                                <input
                                                    type="text"
                                                    value={editingLabel}
                                                    onChange={(e) => setEditingLabel(e.target.value)}
                                                    className="edit-input"
                                                    autoFocus
                                                />
                                                <button 
                                                    onClick={() => updateLabel(item.ip, editingLabel)}
                                                    className="save-button"
                                                >
                                                    ✓
                                                </button>
                                                <button 
                                                    onClick={cancelEditing}
                                                    className="cancel-button"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="label-display">
                                                <span className={`label-text ${!item.label ? 'empty' : ''}`}>
                                                    {item.label || 'Sense label'}
                                                </span>
                                                <button 
                                                    onClick={() => startEditing(item.ip, item.label)}
                                                    className="edit-button"
                                                    title="Editar label"
                                                >
                                                    ✏️
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="date-cell">
                                        {new Date(item.addedAt).toLocaleDateString('ca-ES')}
                                    </td>
                                    <td className="ip-watchlist-actions-cell">
                                        <button 
                                            onClick={() => removeFromWatchlist(item.ip)}
                                            className="remove-button"
                                            title="Eliminar de la watchlist"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default IpWatchlist;